import React, { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Box,
  Button,
  Table,
  Tbody,
  Text,
  Th,
  Td,
  Thead,
  Tr,
  IconButton,
  useColorModeValue,
  useToast,
  SimpleGrid,
  HStack,
  Divider,
  Badge,
  Progress,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Input,
  Select,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Tooltip } from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import ProductPlannerApi from "../../api/productPlanner";
import OptimizePurchaseModal from "./OptimizePurchaseModal";
import AsyncSelect from "react-select/async/dist/react-select-async.esm";
import ProductApi from "../../api/product";
import { StatsIcon, ClockIcon } from "components/Icons/Icons";
import ProductAnalyticsModal from "./ProductAnalyticsModal";
import SalesForecastModal from "./SalesForecastModal";

function PurchasePlanner() {
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [form, setForm] = useState({
    product_id: "",
    price: "",
    needed_count: "",
    region: "us",
    discount_end_date: "",
  });
  const [isOptimizeModalOpen, setIsOptimizeModalOpen] = useState(false);
  const [optimize, setOptimize] = useState({ budget: "", max_items: "" });
  const [optimizeResult, setOptimizeResult] = useState(null);
  const [loadingOptimize, setLoadingOptimize] = useState(false);
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();
  const [productSelect, setProductSelect] = useState(null);
  const { isOpen: isDeactivateOpen, onOpen: onDeactivateOpen, onClose: onDeactivateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isDeactivateItemOpen, onOpen: onDeactivateItemOpen, onClose: onDeactivateItemClose } = useDisclosure();
  const cancelRef = React.useRef();
  const [productToDelete, setProductToDelete] = useState(null);
  const [productToDeactivate, setProductToDeactivate] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsProduct, setAnalyticsProduct] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [forecastProductId, setForecastProductId] = useState(null);
  const [forecastProductName, setForecastProductName] = useState(null);

  const currencyFormatter = (currency) =>
    new Intl.NumberFormat(currency === "USD" ? "en-US" : "tr-TR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });

  const totals = useMemo(() => {
    const initial = {
      USD: { required: 0, spent: 0, remaining: 0 },
      TRY: { required: 0, spent: 0, remaining: 0 },
    };
    for (const p of products) {
      const region = (p.region || "").toLowerCase();
      const currency = region === "us" ? "USD" : region === "tr" ? "TRY" : null;
      if (!currency) continue;
      const price = Number(p.price) || 0;
      const needed = Number(p.needed_count) || 0;
      const purchased = Number(p.purchased_count) || 0;
      initial[currency].required += price * needed;
      initial[currency].spent += price * purchased;
    }
    initial.USD.remaining = Math.max(initial.USD.required - initial.USD.spent, 0);
    initial.TRY.remaining = Math.max(initial.TRY.required - initial.TRY.spent, 0);
    return initial;
  }, [products]);

  const usdPercent = useMemo(() => {
    return totals.USD.required ? Math.min((totals.USD.spent / totals.USD.required) * 100, 100) : 0;
  }, [totals]);
  const tryPercent = useMemo(() => {
    return totals.TRY.required ? Math.min((totals.TRY.spent / totals.TRY.required) * 100, 100) : 0;
  }, [totals]);

  const tryCountPercent = useMemo(() => {
    const needed = products.filter(p => (p.region || '').toLowerCase() === 'tr').reduce((sum, p) => sum + Number(p.needed_count || 0), 0);
    const purchased = products.filter(p => (p.region || '').toLowerCase() === 'tr').reduce((sum, p) => sum + Number(p.purchased_count || 0), 0);
    return needed ? Math.min((purchased / needed) * 100, 100) : 0;
  }, [products]);

  const usdCountPercent = useMemo(() => {
    const needed = products.filter(p => (p.region || '').toLowerCase() === 'us').reduce((sum, p) => sum + Number(p.needed_count || 0), 0);
    const purchased = products.filter(p => (p.region || '').toLowerCase() === 'us').reduce((sum, p) => sum + Number(p.purchased_count || 0), 0);
    return needed ? Math.min((purchased / needed) * 100, 100) : 0;
  }, [products]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await ProductPlannerApi.listProducts();
      setProducts(response.data.data || response.data);
      setTotalPages(1);
    } catch (err) {
      toast({ title: "خطا در دریافت داده‌ها", status: "error", position: "top-right" });
    }
  };

  const handleOpenModal = (type, product = null) => {
    setModalType(type);
    setSelectedProduct(product);
    setForm(
      product
        ? { ...product }
        : { product_id: "", price: "", needed_count: "", region: "us", discount_end_date: "" }
    );
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        purchased_count: form.purchased_count === undefined || form.purchased_count === null || form.purchased_count === "" ? 0 : Number(form.purchased_count),
      };
      if (modalType === "add") {
        await ProductPlannerApi.createProduct(payload);
        toast({ title: "محصول اضافه شد", status: "success", position: "top-right" });
      } else {
        await ProductPlannerApi.updateProduct(selectedProduct.id, payload);
        toast({ title: "محصول ویرایش شد", status: "success", position: "top-right" });
      }
      fetchProducts();
      handleCloseModal();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.errors) {
        const errors = err.response.data.errors;
        const messages = Object.values(errors).flat().join("\n");
        toast({
          title: "خطای اعتبارسنجی",
          description: messages,
          status: "error",
          position: "top-right",
          duration: 7000,
          isClosable: true,
        });
      } else {
        toast({ title: "خطا در ذخیره‌سازی", status: "error", position: "top-right" });
      }
    }
  };

  const handleDeleteClick = (id) => {
    setProductToDelete(id);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    try {
      await ProductPlannerApi.deleteProduct(productToDelete);
      toast({ title: "محصول حذف شد", status: "success", position: "top-right" });
      fetchProducts();
      onDeleteClose();
      setProductToDelete(null);
    } catch (err) {
      toast({ title: "خطا در حذف", status: "error", position: "top-right" });
    }
  };

  const handleDeactivateClick = (product) => {
    setProductToDeactivate(product);
    onDeactivateItemOpen();
  };

  const handleDeactivateProduct = async () => {
    try {
      if (!productToDeactivate) return;
      const payload = {
        product_id: productToDeactivate.product_id,
        price: productToDeactivate.price,
        needed_count: productToDeactivate.needed_count,
        purchased_count: productToDeactivate.purchased_count ?? 0,
        region: productToDeactivate.region,
        discount_end_date: productToDeactivate.discount_end_date || null,
        is_active: 0,
      };
      await ProductPlannerApi.updateProduct(productToDeactivate.id, payload);
      toast({ title: "محصول غیرفعال شد", status: "success", position: "top-right" });
      fetchProducts();
      onDeactivateItemClose();
      setProductToDeactivate(null);
    } catch (err) {
      toast({ title: "خطا در غیرفعال‌سازی", status: "error", position: "top-right" });
    }
  };

  // خرید بهینه
  const handleOpenOptimizeModal = () => {
    setIsOptimizeModalOpen(true);
    setOptimize({ budget: "30", max_items: "5" });
    setOptimizeResult(null);
  };
  const handleCloseOptimizeModal = () => {
    setIsOptimizeModalOpen(false);
    setOptimizeResult(null);
  };
  const handleOptimize = async (params = {}) => {
    setLoadingOptimize(true);
    try {
      const budgetValue = params?.budget ?? optimize.budget;
      const maxItemsValue = params?.max_items ?? optimize.max_items;

      if (!budgetValue || !maxItemsValue) {
        toast({ title: "بودجه و حداکثر آیتم را مشخص کنید", status: "warning", position: "top-right" });
        return null;
      }

      const res = await ProductPlannerApi.optimizePurchases(budgetValue, maxItemsValue);
      const resultData = { ...(res.data || {}), budget: budgetValue, max_items: maxItemsValue };
      setOptimizeResult(resultData);
      return resultData;
    } catch (err) {
      toast({ title: "خطا در بهینه‌سازی", status: "error", position: "top-right" });
      return null;
    } finally {
      setLoadingOptimize(false);
    }
  };

  const loadProductOptions = async (inputValue, callback) => {
    try {
      const res = await ProductApi.Products(inputValue || "", 1);
      const options = (res.data.data || []).map((p) => ({
        value: p.id,
        label: p.name,
        full: p,
      }));
      callback(options);
    } catch (e) {
      callback([]);
    }
  };
  const handleProductChange = (option) => {
    setProductSelect(option);
    setForm((prev) => ({
      ...prev,
      product_id: option ? option.value : "",
      product_name: option ? option.label : "",
    }));
  };

  const handleDeactivatePlanner = async () => {
    try {
      await ProductPlannerApi.deactivatePlanner();
      toast({ title: "برنامه خرید با موفقیت اتمام یافت", status: "success", position: "top-right" });
      fetchProducts();
      onDeactivateClose();
    } catch (err) {
      toast({ title: "خطا در اتمام برنامه خرید", status: "error", position: "top-right" });
    }
  };

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader p="6px 0px 22px 0px" display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              برنامه خرید
            </Text>
            <Button colorScheme="blue" onClick={handleOpenOptimizeModal} ml={2}>خرید بهینه</Button>
            <Button colorScheme="red" onClick={onDeactivateOpen} ml={2}>اتمام برنامه خرید</Button>
          </Box>
          <Button colorScheme="teal" onClick={() => handleOpenModal("add")}>افزودن</Button>
        </CardHeader>
        <CardBody pt={0}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5} mt={4} w="100%" alignItems="stretch">
            {/* USD Card */}
            <Box
              borderWidth="1px"
              borderRadius="xl"
              p={5}
              bg={useColorModeValue("white", "gray.800")}
              borderColor={useColorModeValue("gray.200", "whiteAlpha.300")}
              boxShadow={useColorModeValue("sm", "sm")}
              h="100%"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold">🇺🇸 آمریکا</Text>
                  <Badge colorScheme="purple" variant="subtle">USD</Badge>
                </HStack>
                <Badge colorScheme={totals.USD.remaining === 0 ? "green" : "yellow"}>
                  {totals.USD.remaining === 0 ? "تکمیل" : "در حال خرید"}
                </Badge>
              </HStack>
              <Divider mb={4} />
              {/* Clear, explicit stats row for USA */}
              <SimpleGrid columns={{ base: 1, sm: 5 }} spacing={4} mt={4}>
                <Stat>
                  <StatLabel>مبلغ مورد نیاز</StatLabel>
                  <StatNumber>{currencyFormatter("USD").format(totals.USD.required)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>مبلغ خرج شده</StatLabel>
                  <StatNumber>{currencyFormatter("USD").format(totals.USD.spent)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>مبلغ باقی‌مانده</StatLabel>
                  <StatNumber color={totals.USD.remaining === 0 ? "green.500" : "orange.500"}>
                    {currencyFormatter("USD").format(totals.USD.remaining)}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>تعداد موردنیاز</StatLabel>
                  <StatNumber>{products.filter(p => (p.region || '').toLowerCase() === 'us').reduce((sum, p) => sum + Number(p.needed_count || 0), 0)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>خریداری‌شده</StatLabel>
                  <StatNumber>{products.filter(p => (p.region || '').toLowerCase() === 'us').reduce((sum, p) => sum + Number(p.purchased_count || 0), 0)}</StatNumber>
                </Stat>
              </SimpleGrid>
              <Box mt={4}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.500">پیشرفت خرید</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">{usdCountPercent.toFixed(0)}%</Text>
                </HStack>
                <Progress
                  value={usdCountPercent}
                  size="sm"
                  colorScheme="purple"
                  borderRadius="full"
                  hasStripe
                />
              </Box>
            </Box>

            <Box
              borderWidth="1px"
              borderRadius="xl"
              p={5}
              bg={useColorModeValue("white", "gray.800")}
              borderColor={useColorModeValue("gray.200", "whiteAlpha.300")}
              boxShadow={useColorModeValue("sm", "sm")}
              h="100%"
            >
              <HStack justify="space-between" mb={3}>
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold">🇹🇷 ترکیه</Text>
                  <Badge colorScheme="purple" variant="subtle">TRY</Badge>
                </HStack>
                <Badge colorScheme={totals.TRY.remaining === 0 ? "green" : "yellow"}>
                  {totals.TRY.remaining === 0 ? "تکمیل" : "در حال خرید"}
                </Badge>
              </HStack>
              <Divider mb={4} />
              {/* Clear, explicit stats row for Turkey */}
              <SimpleGrid columns={{ base: 1, sm: 5 }} spacing={4} mt={4}>
                <Stat>
                  <StatLabel>مبلغ مورد نیاز</StatLabel>
                  <StatNumber>{currencyFormatter("TRY").format(totals.TRY.required)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>مبلغ خرج شده</StatLabel>
                  <StatNumber>{currencyFormatter("TRY").format(totals.TRY.spent)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>مبلغ باقی‌مانده</StatLabel>
                  <StatNumber color={totals.TRY.remaining === 0 ? "green.500" : "orange.500"}>
                    {currencyFormatter("TRY").format(totals.TRY.remaining)}
                  </StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>تعداد موردنیاز</StatLabel>
                  <StatNumber>{products.filter(p => (p.region || '').toLowerCase() === 'tr').reduce((sum, p) => sum + Number(p.needed_count || 0), 0)}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>خریداری‌شده</StatLabel>
                  <StatNumber>{products.filter(p => (p.region || '').toLowerCase() === 'tr').reduce((sum, p) => sum + Number(p.purchased_count || 0), 0)}</StatNumber>
                </Stat>
              </SimpleGrid>
              <Box mt={4}>
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" color="gray.500">پیشرفت خرید</Text>
                  <Text fontSize="sm" color="gray.600" fontWeight="semibold">{tryCountPercent.toFixed(0)}%</Text>
                </HStack>
                <Progress
                  value={tryCountPercent}
                  size="sm"
                  colorScheme="purple"
                  borderRadius="full"
                  hasStripe
                />
              </Box>
            </Box>
          </SimpleGrid>
        </CardBody>
        <CardBody>
          <Table variant="simple" color={textColor} mt={4}>
            <Thead>
              <Tr my=".8rem" pl="0px" color="gray.400">
                <Th color="gray.400">نام محصول</Th>
                <Th color="gray.400">قیمت</Th>
                <Th color="gray.400">تعداد مورد نیاز</Th>
                <Th color="gray.400">تعداد خریداری شده</Th>
                <Th color="gray.400">منطقه</Th>
                <Th color="gray.400">واحد پول</Th>
                <Th color="gray.400">تاریخ پایان تخفیف</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {products.map((product) => {
                const isCompleted = Number(product.purchased_count) >= Number(product.needed_count);
                return (
                  <Tr key={product.id} bg={isCompleted ? "green.50" : "transparent"}>
                    <Td>{product.product_name}</Td>
                    <Td>{product.price}</Td>
                    <Td>{product.needed_count}</Td>
                    <Td color={isCompleted ? "green.600" : "inherit"} fontWeight={isCompleted ? "bold" : "normal"}>
                      {product.purchased_count}
                    </Td>
                    <Td>{product.region}</Td>
                    <Td>{product.currency}</Td>
                    <Td>{product.discount_end_date}</Td>
                    <Td textAlign="center">
                      <IconButton
                        size="sm"
                        colorScheme="blue"
                        aria-label="Edit"
                        icon={<EditIcon />}
                        onClick={() => handleOpenModal("edit", product)}
                        mr={1}
                      />
                      <Tooltip label="غیرفعال سازی">
                        <IconButton
                          size="sm"
                          colorScheme="orange"
                          aria-label="Deactivate"
                          icon={<ViewOffIcon />}
                          onClick={() => handleDeactivateClick(product)}
                          mr={1}
                        />
                      </Tooltip>
                      <IconButton
                        size="sm"
                        colorScheme="purple"
                        aria-label="آمار فروش"
                        icon={<StatsIcon />}
                        mr={1}
                        onClick={() => {
                          setShowAnalytics(true);
                          setAnalyticsProduct({ id: product.product_id || product.id, name: product.product_name });
                        }}
                        title="آمار فروش"
                      />
                      <IconButton
                        size="sm"
                        colorScheme="teal"
                        aria-label="پیش بینی فروش"
                        icon={<ClockIcon />}
                        mr={1}
                        onClick={() => {
                          const pid = product.product_id || product.id;
                          setForecastProductId(pid);
                          setForecastProductName(product.product_name);
                          setShowForecast(true);
                        }}
                        title="پیش بینی فروش"
                      />
                      <IconButton
                        size="sm"
                        colorScheme="red"
                        aria-label="Delete"
                        icon={<DeleteIcon />}
                        onClick={() => handleDeleteClick(product.id)}
                        mr={1}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </CardBody>
        
      </Card>

      <OptimizePurchaseModal
        isOpen={isOptimizeModalOpen}
        onClose={handleCloseOptimizeModal}
        onOptimize={handleOptimize}
        optimize={optimize}
        setOptimize={setOptimize}
        optimizeResult={optimizeResult}
        loading={loadingOptimize}
      />
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{modalType === "add" ? "افزودن محصول" : "ویرایش محصول"}</ModalHeader>
          <ModalCloseButton position="absolute" left="8px" right="unset" />
          <ModalBody>
            <Box display="flex" flexWrap="wrap" gap={4}>
              <Box flex="1 1 100%">
                <FormControl mb={3}>
                  <FormLabel>انتخاب محصول</FormLabel>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    value={productSelect}
                    loadOptions={loadProductOptions}
                    onChange={handleProductChange}
                    placeholder="جستجو و انتخاب محصول..."
                    isClearable
                  />
                </FormControl>
              </Box>
              <Box flex="1 1 45%">
                <FormControl mb={3}>
                  <FormLabel>قیمت</FormLabel>
                  <NumberInput min={0} value={form.price} onChange={(val) => setForm((prev) => ({ ...prev, price: val }))}>
                    <NumberInputField name="price" />
                  </NumberInput>
                </FormControl>
              </Box>
              <Box flex="1 1 45%">
                <FormControl mb={3}>
                  <FormLabel>تعداد مورد نیاز</FormLabel>
                  <NumberInput min={1} value={form.needed_count} onChange={(val) => setForm((prev) => ({ ...prev, needed_count: val }))}>
                    <NumberInputField name="needed_count" />
                  </NumberInput>
                </FormControl>
              </Box>
              <Box flex="1 1 45%">
                <FormControl mb={3}>
                  <FormLabel>تعداد خریداری شده</FormLabel>
                  <NumberInput min={0} value={form.purchased_count || 0} onChange={(val) => setForm((prev) => ({ ...prev, purchased_count: val }))}>
                    <NumberInputField name="purchased_count" />
                  </NumberInput>
                </FormControl>
              </Box>
              <Box flex="1 1 45%">
                <FormControl mb={3}>
                  <FormLabel>منطقه</FormLabel>
                  <Select name="region" value={form.region} onChange={handleFormChange}>
                    <option value="us">آمریکا</option>
                    <option value="tr">ترکیه</option>
                  </Select>
                </FormControl>
              </Box>
              <Box flex="1 1 45%">
                <FormControl mb={3}>
                  <FormLabel>تاریخ پایان تخفیف</FormLabel>
                  <Input name="discount_end_date" value={form.discount_end_date} onChange={handleFormChange} placeholder="YYYY-MM-DD" />
                </FormControl>
              </Box>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSubmit}>
              ذخیره
            </Button>
            <Button onClick={handleCloseModal}>انصراف</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal for Analytics */}
      <ProductAnalyticsModal
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        product={analyticsProduct}
      />

      {/* Modal for Forecast */}
      <SalesForecastModal
        isOpen={showForecast}
        onClose={() => setShowForecast(false)}
        productId={forecastProductId}
        productName={forecastProductName}
        defaultForecastDays={60}
        defaultHistoryDays={360}
      />

        <AlertDialog
          isOpen={isDeactivateOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeactivateClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                تأیید اتمام برنامه خرید
              </AlertDialogHeader>
              <AlertDialogBody>
                آیا مطمئن هستید که می‌خواهید برنامه خرید را اتمام کنید؟ این عمل غیرقابل بازگشت است.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeactivateClose}>
                  لغو
                </Button>
                <Button colorScheme="red" onClick={handleDeactivatePlanner} ml={3}>
                  تأیید
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <AlertDialog
          isOpen={isDeactivateItemOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeactivateItemClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                تأیید غیرفعال‌سازی محصول
              </AlertDialogHeader>
              <AlertDialogBody>
                آیا مطمئن هستید که می‌خواهید این محصول را غیرفعال کنید؟
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeactivateItemClose}>
                  لغو
                </Button>
                <Button colorScheme="orange" onClick={handleDeactivateProduct} ml={3}>
                  غیرفعال‌سازی
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <AlertDialog
          isOpen={isDeleteOpen}
          leastDestructiveRef={cancelRef}
          onClose={onDeleteClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                تأیید حذف محصول
              </AlertDialogHeader>
              <AlertDialogBody>
                آیا مطمئن هستید که می‌خواهید این محصول را از برنامه خرید حذف کنید؟ این عمل غیرقابل بازگشت است.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onDeleteClose}>
                  لغو
                </Button>
                <Button colorScheme="red" onClick={handleDelete} ml={3}>
                  حذف
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
    </Flex>
  );
}

export default PurchasePlanner;

